import { parseThemeColors } from '../gameTemplates';

export const scenarioBasedTemplate = (data) => {
  const { title, questions, themeColors, themeFonts, backgroundImage } = data;
  const colors = parseThemeColors(themeColors);
  
  return `const InteractiveElement = () => {
  const [currentScreen, setCurrentScreen] = useState("start");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const traceId = useRef(null);

  const questions = ${JSON.stringify(questions, null, 2)};

  const handleBegin = () => {
    try {
    traceId.current = crypto.randomUUID();
      const startTimeStamp = Date.now();
      setStartTime(new Date());
      
    trackEvent('interactive_start', traceId.current, {
        startTime: startTimeStamp,
      status: 'started'
    });
    
    window.parent.postMessage({
      type: 'interactive_start',
      data: {
        traceId: traceId.current,
          startTime: startTimeStamp,
          status: 'started',
          url: window.location.href
      }
    }, '*');

      setCurrentScreen('playing');
    } catch (error) {
      console.error('Error starting interactive:', error);
      setCurrentScreen('playing');
    }
  };

  const handleNext = () => {
    if (selectedOption === null) return;
    const isCorrect = selectedOption === questions[currentQuestionIndex].correctIndex;
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = { selectedIndex: selectedOption, isCorrect };
    setUserAnswers(newAnswers);
    setCurrentScreen('validation');
  };

  const handleSkip = () => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = { selectedIndex: null, isCorrect: false };
    setUserAnswers(newAnswers);
    setCurrentScreen('validation');
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex === questions.length - 1) {
      setCurrentScreen('summary');
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption(null);
      setCurrentScreen('playing');
    }
  };

  const handleRestart = () => {
    setCurrentScreen('start');
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setUserAnswers(new Array(questions.length).fill({ selectedIndex: null, isCorrect: null }));
    setStartTime(null);
    traceId.current = null;
  };

  const formatTime = (totalSeconds) => {
    if (!totalSeconds || totalSeconds < 0) return '0s';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return \`\${minutes}m \${seconds}s\`;
    }
    return \`\${seconds}s\`;
  };

  const answered = userAnswers.filter(ua => ua.selectedIndex !== null).length;
  const totalCorrect = userAnswers.filter(ua => ua.isCorrect === true).length;
  const totalTime = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
  const scorePercentage = questions.length > 0 ? Math.round((totalCorrect / questions.length) * 100) : 0;

  useEffect(() => {
    if (currentScreen === 'summary' && startTime && traceId.current) {
      try {
        const endTimeStamp = Date.now();
        const timeTaken = Math.floor((endTimeStamp - startTime.getTime()) / 1000);
        
        const questionsData = questions.map((q, index) => ({
          id: index + 1,
          question: q.question,
          correctAnswer: q.options[q.correctIndex],
          userAnswer: userAnswers[index].selectedIndex !== null ? q.options[userAnswers[index].selectedIndex] : 'Skipped',
          isCorrect: userAnswers[index].isCorrect || false,
          explanation: q.explanation
        }));

        const eventData = {
          endTime: endTimeStamp,
      timeTaken: timeTaken,
          totalQuestions: questions.length,
          totalScore: questions.length,
          userScore: totalCorrect,
          status: 'completed',
          questions: questionsData,
          summary: {
            totalCorrect: totalCorrect,
            totalIncorrect: questions.length - totalCorrect,
            accuracy: scorePercentage
          }
        };

        trackEvent('interactive_end', traceId.current, eventData);
    
    window.parent.postMessage({
      type: 'interactive_end',
      data: {
        traceId: traceId.current,
            ...eventData,
            url: window.location.href
          }
        }, '*');
      } catch (error) {
        console.error('Error tracking interactive end:', error);
      }
    }
  }, [currentScreen, startTime, totalCorrect, questions, userAnswers, scorePercentage]);

  return (
    <div 
      className="relative min-h-screen bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: 'url(${backgroundImage})',
        fontFamily: '${themeFonts}'
      }}
    >
      <div className="relative z-10 min-h-screen flex items-center justify-center">
        {currentScreen === "start" && (
          <div className="w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="flex flex-col items-center justify-center gap-6 py-16 px-6">
              <h1 className="text-[${colors.primary}] text-[28px] font-bold leading-[125%] tracking-[-0.022rem] text-center">
                ${title}
              </h1>
              <p className="text-[#4E4E4E] text-[20px] font-medium leading-[125%] tracking-[-0.0165rem] text-center">
                Explore how to handle various scenarios.
              </p>
              <button
                onClick={handleBegin}
                className="w-44 sm:w-[252px] h-10 sm:h-[48px] rounded-md hover:opacity-90 focus:outline-none flex items-center justify-center"
                style={{ backgroundColor: '${colors.primary}' }}
              >
                <span className="text-white text-[20px] font-semibold leading-[125%] tracking-[-0.0165rem]">
                  Begin
                </span>
              </button>
            </div>
          </div>
        )}

        {(currentScreen === 'playing' || currentScreen === 'validation') && (
          <div className="w-[960px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            {currentScreen === 'playing' && questions.length > 0 && (
              <>
                <div className="w-full h-2 bg-zinc-200 absolute top-0 left-0 rounded-t-2xl">
                  <div 
                    className="h-2 rounded-tl-2xl transition-all"
                    style={{ 
                      width: \`\${((currentQuestionIndex + 1) / questions.length) * 100}%\`,
                      backgroundColor: '${colors.primary}'
                    }}
                  />
                </div>
                
                <div className="pt-2 pb-4 px-[40px]">
                  <div className="pt-[32px] pb-5">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-[${colors.primary}] text-2xl font-bold">Scenario {currentQuestionIndex + 1}</div>
                      <div className="w-14 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '${colors.secondary}20' }}>
                        <div className="text-[${colors.secondary}] text-lg font-semibold">{currentQuestionIndex + 1}/{questions.length}</div>
                      </div>
                    </div>
                    <div className="text-neutral-600 text-xl font-medium leading-[125%] mb-3" dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(questions[currentQuestionIndex].scenario) 
                    }}></div>
                    <div className="text-neutral-600 text-lg font-medium leading-[125%]" dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(questions[currentQuestionIndex].question) 
                    }}></div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    {questions[currentQuestionIndex].options.map((option, index) => (
                      <button 
                        key={index}
                        onClick={() => setSelectedOption(index)} 
                        className="w-full p-2.5 rounded-lg flex items-center gap-2.5 transition-all"
                        style={{
                          backgroundColor: selectedOption === index ? '#E2E8F0' : 'transparent',
                          border: selectedOption === index ? 'none' : '2px solid #E2E8F0'
                        }}
                      >
                        <div className="w-7 h-7 relative flex-shrink-0">
                          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                            <circle cx="14" cy="14" r="14" fill={selectedOption === index ? '${colors.primary}' : '#E0DEEF'}/>
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold" 
                               style={{color: selectedOption === index ? 'white' : '${colors.primary}'}}>
                            {String.fromCharCode(65 + index)}
                          </div>
                        </div>
                        <div className="flex-1 text-left text-lg font-normal leading-[125%]" 
                             style={{color: selectedOption === index ? '${colors.primary}' : '#374151'}}>
                          <span dangerouslySetInnerHTML={{ 
                            __html: DOMPurify.sanitize(option) 
                          }}></span>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex gap-3">
                    <button 
                      onClick={handleNext} 
                      disabled={selectedOption === null}
                      className="flex-1 h-12 rounded-lg flex justify-center items-center transition-all"
                      style={{
                        backgroundColor: selectedOption !== null ? '${colors.primary}' : '#94A3B8',
                        opacity: selectedOption === null ? 0.7 : 1
                      }}
                    >
                      <span className="text-white text-xl font-semibold">Next →</span>
                    </button>
                    <button 
                      onClick={handleSkip} 
                      className="w-32 h-12 rounded-lg flex justify-center items-center"
                      style={{ backgroundColor: '${colors.primary}20' }}
                    >
                      <span className="text-[${colors.primary}] text-xl font-semibold">Skip</span>
                    </button>
                  </div>
                </div>
              </>
            )}

            {currentScreen === 'validation' && questions.length > 0 && (
              <>
                <div className="w-full h-2 bg-zinc-200 absolute top-0 left-0 rounded-t-2xl">
                  <div 
                    className="h-2 rounded-tl-2xl transition-all"
                    style={{ 
                      width: \`\${((currentQuestionIndex + 1) / questions.length) * 100}%\`,
                      backgroundColor: '${colors.primary}'
                    }}
                  />
                </div>
                
                <div className="pt-2 pb-4 px-[40px]">
                  <div className="pt-[32px] pb-5">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-[${colors.primary}] text-2xl font-bold">Scenario {currentQuestionIndex + 1}</div>
                      <div className="w-14 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '${colors.secondary}20' }}>
                        <div className="text-[${colors.secondary}] text-lg font-semibold">{currentQuestionIndex + 1}/{questions.length}</div>
                      </div>
                    </div>
                    <div className="text-neutral-600 text-xl font-medium leading-[125%] mb-3" dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(questions[currentQuestionIndex].scenario) 
                    }}></div>
                    <div className="text-neutral-600 text-lg font-medium leading-[125%]" dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(questions[currentQuestionIndex].question) 
                    }}></div>
                  </div>
                  
                  <div className="mb-4">
                    {userAnswers[currentQuestionIndex].selectedIndex !== null && (
                      <div className="w-full p-2.5 rounded-lg flex items-center gap-2.5 mb-3"
                           style={{border: userAnswers[currentQuestionIndex].isCorrect ? '2px solid #E2E8F0' : '2px solid #FECACA'}}>
                        <div className="w-7 h-7 relative flex-shrink-0">
                          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                            <circle cx="14" cy="14" r="14" fill={userAnswers[currentQuestionIndex].isCorrect ? "#DEEEEF" : "#EFDEE2"}/>
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold"
                               style={{color: userAnswers[currentQuestionIndex].isCorrect ? '${colors.primary}' : '#78350F'}}>
                            {String.fromCharCode(65 + userAnswers[currentQuestionIndex].selectedIndex)}
                          </div>
                        </div>
                        <div className="flex-1 text-lg font-normal leading-tight"
                             style={{color: userAnswers[currentQuestionIndex].isCorrect ? '${colors.primary}' : '#78350F'}}>
                          <span dangerouslySetInnerHTML={{ 
                            __html: DOMPurify.sanitize(questions[currentQuestionIndex].options[userAnswers[currentQuestionIndex].selectedIndex]) 
                          }}></span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 mb-1">
                      {userAnswers[currentQuestionIndex].selectedIndex === null ? (
                        <>
                          <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">−</span>
                          </div>
                          <span className="text-[${colors.primary}] text-lg font-bold">Skipped</span>
                        </>
                      ) : userAnswers[currentQuestionIndex].isCorrect ? (
                        <>
                          <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">✓</span>
                          </div>
                          <span className="text-[${colors.primary}] text-lg font-bold">Correct answer</span>
                        </>
                      ) : (
                        <>
                          <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">✗</span>
                          </div>
                          <span className="text-[${colors.primary}] text-lg font-bold">Incorrect answer</span>
                        </>
                      )}
                    </div>
                    
                    <div className="text-neutral-600 text-lg font-normal leading-[125%] mb-4">
                      <span dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(questions[currentQuestionIndex].explanation) 
                      }}></span>
                    </div>
                    
                    {!userAnswers[currentQuestionIndex].isCorrect && userAnswers[currentQuestionIndex].selectedIndex !== null && (
                      <>
                        <div className="text-[${colors.primary}] text-lg font-bold mb-1">Correct answer</div>
                        <div className="w-full p-2.5 rounded-lg outline outline-2 outline-offset-[-2px] outline-slate-200 flex items-center gap-2.5">
                          <div className="w-7 h-7 relative flex-shrink-0">
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                              <circle cx="14" cy="14" r="14" fill="#DEEEEF"/>
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center text-[${colors.primary}] text-sm font-semibold">
                              {String.fromCharCode(65 + questions[currentQuestionIndex].correctIndex)}
                            </div>
                          </div>
                          <div className="flex-1 text-[${colors.primary}] text-lg font-normal leading-[125%]">
                            <span dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(questions[currentQuestionIndex].options[questions[currentQuestionIndex].correctIndex]) 
                            }}></span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <button 
                    onClick={handleNextQuestion} 
                    className="w-full h-12 rounded-lg flex justify-center items-center"
                    style={{ backgroundColor: '${colors.primary}' }}
                  >
                    <span className="text-white text-xl font-semibold">
                      {currentQuestionIndex === questions.length - 1 ? 'Show Summary' : 'Next →'}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {currentScreen === 'summary' && (
          <div className="w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            {scorePercentage === 100 && <Confetti />}

            <div className="p-8">
              <h2 className="text-[${colors.primary}] text-[28px] font-bold tracking-[-0.022rem] text-center">
                Your Results
              </h2>
              <hr className="my-4 w-1/2 mx-auto border-t-[#E0DEEF]" />

              <div className="px-1.5 sm:px-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                    <div className="text-[#515151] text-xs mb-0.5 tracking-wide">Final Score</div>
                    <div className="text-[${colors.primary}] text-2xl font-bold">{scorePercentage}%</div>
                  </div>
                  <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                    <div className="text-[#515151] text-xs mb-0.5 tracking-wide">Total Time</div>
                    <div className="text-[${colors.primary}] text-2xl font-bold">{formatTime(totalTime)}</div>
                  </div>
                  <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                    <div className="text-[#515151] text-xs mb-0.5 tracking-wide">Answered</div>
                    <div className="text-[${colors.primary}] text-2xl font-bold">
                      {answered}/{questions.length}
                    </div>
                  </div>
                </div>

                <div className="space-y-2.5 mb-6">
                  {questions.map((q, idx) => {
                    const ua = userAnswers[idx];
                    const isCorrect = ua.isCorrect;
                    const isSkipped = ua.selectedIndex === null;

                    const bgTint = isSkipped
                      ? 'bg-yellow-50'
                      : isCorrect
                      ? 'bg-green-50'
                      : 'bg-red-50';

                    const icon = isSkipped ? '−' : isCorrect ? '✓' : '✗';

                    const ans = isSkipped
                      ? 'Skipped | Correct: ' + String.fromCharCode(65 + q.correctIndex)
                      : isCorrect
                      ? 'Your answer: ' + String.fromCharCode(65 + ua.selectedIndex)
                      : 'Your answer: ' + String.fromCharCode(65 + ua.selectedIndex) + ' | Correct: ' + String.fromCharCode(65 + q.correctIndex);

                    return (
                      <div
                        key={idx}
                        className={\`\${bgTint} border border-[#E0DEEF] p-3 rounded-xl flex gap-3\`}
                      >
                        <div 
                          className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center"
                          style={{ backgroundColor: '${colors.primary}' }}
                        >
                          {icon}
                        </div>
                        <div className="flex-1">
                          <p className="text-[${colors.primary}] text-base font-medium leading-tight mb-2">
                            Q{idx + 1}: <span dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(q.scenario) 
                            }}></span>
                          </p>
                          <p className="text-[${colors.primary}] text-md font-medium leading-tight mb-1">
                            <span dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(q.question) 
                            }}></span>
                          </p>
                          <p className="text-[#4e4e4e] text-md">{ans}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleRestart}
                  className="w-44 h-12 rounded-md text-white text-[20px] font-semibold hover:opacity-90"
                  style={{ backgroundColor: '${colors.primary}' }}
                >
                  Restart Quiz
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};`;
};